import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Radio,
  FormControlLabel,
  TextField,
  Grid,
  Tab,
  Tabs,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDataProvider, useNotify, useRedirect } from "react-admin";

const RTP_CONFIGS = [85, 88, 90, 92, 95, 98];
// Function to get RTP types based on game name
const getRTPTypes = (gameName) => {
  if (gameName === "Wolf Run") {
    return ["RTP_Default", "RTP_2X"];
  }
  if (gameName === "Buffalo Gold Rush") {
    return ["RTP_Default", "RTP_2X", "RTP_3X", "RTP_5X", "RTP_8X", "RTP_9X", "RTP_10X", "RTP_15X", "RTP_25X", "RTP_Firelink"];
  }
  return ["RTP_Default", "RTP_2X", "RTP_3X", "RTP_5X", "RTP_8X", "RTP_9X", "RTP_10X", "RTP_15X", "RTP_25X", "RTP_Firelink"];
};

// Function to get symbols based on game name
const getSymbols = (gameName) => {
  if (gameName === "Wolf Run") {
    return {
      lowerSymbols: ["L1", "L2", "L3", "L4", "L5"],
      midSymbols: ["M1", "M2"],
      higherSymbols: ["H1", "H2"],
      specialSymbols: ["Scatter", "Wild"]
    };
  }
  if (gameName === "Buffalo Gold Rush") {
    return {
      lowerSymbols: ["N10"],
      midSymbols: ["A", "K", "Q", "J"],
      higherSymbols: ["H1", "H2", "M1", "M2", "M3"],
      specialSymbols: ["Scatter", "Wild"]
    };
  }
  return {
    lowerSymbols: ["N9", "N10"],
    midSymbols: ["A", "J", "K", "Q"],
    higherSymbols: ["H1", "H2", "H3"],
    specialSymbols: ["Scatter", "Wild", "Firelink"]
  };
};

const REELS = ["reel1", "reel2", "reel3", "reel4", "reel5"];

// Lazy Symbol Input Component - only renders when needed
const SymbolInputs = ({ rtp, rtpType, selectedTab, values, setValue, symbols }) => {
  const { lowerSymbols, midSymbols, higherSymbols, specialSymbols } = symbols;
  
  const renderSymbolGroup = (symbolGroup) => (
    symbolGroup.map((symbol) => (
      <Grid item xs={6} sm={4} key={symbol}>
        <TextField
          label={`Symbol ${symbol}`}
          fullWidth
          value={values[`${rtp}_${rtpType}_${REELS[selectedTab]}_${symbol}`] || ""}
          onChange={(e) => {
            const newValue = e.target.value;
            if (newValue === "" || (!isNaN(newValue) && Number(newValue) <= 200)) {
              setValue(rtp, rtpType, REELS[selectedTab], symbol, newValue);
            }
          }}
          inputProps={{
            autoComplete: "off",
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
        />
      </Grid>
    ))
  );

  return (
    <Grid container spacing={2} mt={1}>
      {renderSymbolGroup(lowerSymbols)}
      {renderSymbolGroup(midSymbols)}
      {renderSymbolGroup(higherSymbols)}
      {renderSymbolGroup(specialSymbols)}
    </Grid>
  );
};

const FirelinkGameRTPForm = ({ selectedGame, editMode = false, record = null }) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const [selectedRTP, setSelectedRTP] = useState("85");
  const [expandedRTP, setExpandedRTP] = useState(null);
  const [expandedRTPType, setExpandedRTPType] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);

  // Get game-specific configurations
  const rtpTypes = getRTPTypes(selectedGame?.name);
  const symbols = getSymbols(selectedGame?.name);
  const { lowerSymbols, midSymbols, higherSymbols, specialSymbols } = symbols;

  const setValue = (rtp, rtpType, reel, symbol, value) => {
    setValues(prev => ({
      ...prev,
      [`${rtp}_${rtpType}_${reel}_${symbol}`]: value
    }));
  };

  // useEffect to populate data in edit mode
  useEffect(() => {
    if (editMode && record) {
      setSelectedRTP(record.gameRtp.toString());
      
      // Populate values from firelinkReelName if it exists
      if (record.firelinkReelName) {
        const populatedValues = {};
        record.firelinkReelName.forEach((rtpType) => {
          rtpType.reelData.forEach((reel) => {
            Object.entries(reel.symbols).forEach(([symbol, value]) => {
              const key = `${record.gameRtp}_${rtpType.rtpTypeName}_reel${reel.reelNumber}_${symbol}`;
              populatedValues[key] = value;
            });
          });
        });
        setValues(populatedValues);
      }
    }
  }, [editMode, record]);

  // Submit function for Firelink Game RTP
  const handleFirelinkSubmit = async () => {
    if (!selectedGame) {
      notify("Please select a game.", { type: "error" });
      return;
    }

    setLoading(true);

    try {
      // Convert values to the required format
      const firelinkReelName = rtpTypes.map((rtpType) => ({
        rtpTypeName: rtpType,
        isActive: true,
        reelData: REELS.map((reel, index) => ({
          reelNumber: index + 1,
          symbols: [...lowerSymbols, ...midSymbols, ...higherSymbols, ...specialSymbols].reduce((acc, symbol) => {
            const key = `${selectedRTP}_${rtpType}_${reel}_${symbol}`;
            acc[symbol] = values[key] || "";
            return acc;
          }, {})
        }))
      }));

      // Debug: Log the selectedGame object to see its structure
      console.log("selectedGame object:", selectedGame);
      
      const formattedData = {
        gameName: selectedGame?.name,
        gameRtp: Number(selectedRTP),
        firelinkReelName: firelinkReelName,
      };

      // Only include gameId for create mode, not edit mode
      if (!editMode) {
        formattedData.gameId = selectedGame?.id || selectedGame?.objectId;
      }
      
      console.log("formattedData:", formattedData);

      let response;
      if (editMode && record) {
        // Update existing record
        formattedData.id = record.id;
        response = await dataProvider.update("firelinkGameRtp", {
          id: record.id,
          data: formattedData,
        });
        
        if (response) {
          notify("Firelink Game RTP updated successfully!", {
            type: "success",
          });
          redirect("/gameRtp");
        }
      } else {
        // Create new record
        response = await dataProvider.create("firelinkGameRtp", {
          data: formattedData,
        });

        if (response) {
          notify("Firelink Game RTP created successfully!", {
            type: "success",
          });
          redirect("/gameRtp");
        }
      }
    } catch (error) {
      notify(error.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {RTP_CONFIGS.map((rtp, rtpIndex) => (
        <Accordion
          key={rtp}
          expanded={expandedRTP === rtpIndex}
          onChange={(e, isExpanded) => setExpandedRTP(isExpanded ? rtpIndex : null)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <FormControlLabel
              control={
                <Radio
                  checked={selectedRTP === rtp.toString()}
                  onChange={(e) => setSelectedRTP(e.target.value)}
                  value={rtp.toString()}
                />
              }
              label={<Typography>{rtp}% RTP</Typography>}
              onClick={(e) => e.stopPropagation()}
            />
          </AccordionSummary>
          
          {/* Only render content when this RTP is expanded */}
          {expandedRTP === rtpIndex && (
            <AccordionDetails>
              {rtpTypes.map((rtpType, typeIndex) => (
                <Accordion
                  key={rtpType}
                  expanded={expandedRTPType === `${rtpIndex}_${typeIndex}`}
                  onChange={(e, isExpanded) => setExpandedRTPType(isExpanded ? `${rtpIndex}_${typeIndex}` : null)}
                  sx={{ mt: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{rtpType}</Typography>
                  </AccordionSummary>
                  
                  {/* Only render symbol inputs when this RTP Type is expanded */}
                  {expandedRTPType === `${rtpIndex}_${typeIndex}` && (
                    <AccordionDetails>
                      <Typography variant="subtitle1">Symbol Distribution:</Typography>
                      
                      <Tabs
                        value={selectedTab}
                        onChange={(e, newValue) => setSelectedTab(newValue)}
                        centered
                      >
                        {REELS.map((reel, index) => (
                          <Tab key={reel} label={`Reel ${index + 1}`} />
                        ))}
                      </Tabs>

                      <Typography variant="h6" mt={2}>
                        {`Reel ${selectedTab + 1} Symbol Distribution:`}
                      </Typography>
                      
                      {/* Only render input boxes for the currently expanded section */}
                      <SymbolInputs
                        rtp={rtp}
                        rtpType={rtpType}
                        selectedTab={selectedTab}
                        values={values}
                        setValue={setValue}
                        symbols={symbols}
                      />
                    </AccordionDetails>
                  )}
                </Accordion>
              ))}
            </AccordionDetails>
          )}
        </Accordion>
      ))}
      
      {/* Submit Button */}
      <Button 
        variant="contained" 
        color="primary" 
        onClick={handleFirelinkSubmit}
        disabled={loading}
        sx={{ mt: 3 }}
      >
        Submit
      </Button>
    </div>
  );
};

export default FirelinkGameRTPForm;
